import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, combineLatest, forkJoin, of, take } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { History } from 'src/app/interfaces/history';
import { LicensesHistory } from 'src/app/interfaces/licenses-history';
import { Transactions } from 'src/app/interfaces/transactions';
import { UserProfileData } from 'src/app/interfaces/user-profile-data';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { AuthService } from '../../../Services/Auth/auth.service';
import { CanActivateService } from '../../../Services/CanActivate/can-activate.service';
import { BranchSelectionService } from '../../../Services/branch-selection.service';
import {
  FormControl,
  FormGroupDirective,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AccountsServiceService } from '../../../Services/accounts-service.service';
import { CompanySearch } from '../../../interfaces/company-search.interface';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { TechsupportSelectedCompanyOptionService } from '../../../Services/techsupport-selected-company-option.service';

export class NoResultsErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
    selector: 'app-inventory-page',
    templateUrl: './inventory-page.component.html',
    styleUrls: ['./inventory-page.component.scss'],
    animations: [
        trigger('slideDownInOut', [
            state('void', style({
                opacity: 0,
                transform: 'translateY(-10%)',
            })),
            transition('void => *', animate('600ms ease-out')),
            transition('* => void', animate('600ms ease-in')),
        ]),
    ],
    standalone: false
})
// export class InventoryPageComponent implements OnInit {
export class InventoryPageComponent {
  fiveYearCreditHistory!: History[];
  oneYearCreditHistory!: History[];
  active!: number;
  available!: number;
  expired!: number;
  stacked!: number;
  iconWidth = 18;
  iconHeight = 18;
  loading!: boolean;
  oneYearTotal = 0;
  fiveYearTotal = 0;
  fiveYearRecentTransactions!: Transactions[];
  oneYearRecentTransactions!: Transactions[];
  companyName: string | undefined;
  private destroy$ = new Subject<void>();
  companyId: string | null = null;
  private loadData$ = new Subject<void>();
  form!: UntypedFormGroup;
  options: CompanySearch[] = [];
  noResultsFound: boolean = false;
  userProfileData: UserProfileData | undefined;
  filteredOptions: Observable<CompanySearch[]> | undefined;
  errorStateMatcher: ErrorStateMatcher = new NoResultsErrorStateMatcher();
  currentSection: string = 'inventory'; // Default section

  constructor(
    private backend: BackendAccessService,
    public auth: AuthService,
    private router: Router,
    private branchSelectionService: BranchSelectionService,
    private canActivate: CanActivateService,
    private route: ActivatedRoute,
    private accountsService: AccountsServiceService,
    private loadingService: LoadingService,
    public formBuilder: UntypedFormBuilder,
    public selectedCompanyTS: TechsupportSelectedCompanyOptionService
  ) {}
  /* istanbul ignore next */
  ngOnInit(): void {
    this.initializeComponent();
    this.subscribeToBranchSelectionChanges();
    this.handleSingleBranchScenario();
    this.initializeForm();
  }

  private initializeComponent(): void {
    combineLatest([
      this.canActivate.companyProfile$,
      this.canActivate.multipleBranches$,
    ])
      .pipe(
        take(1),
        tap(([companyProfile, hasMultipleBranches]) => {
          if (companyProfile) {
            console.log('companyProfile', companyProfile);
            this.companyId = companyProfile.companyAccountId;
            this.branchSelectionService.setCompanyName(
              companyProfile.companyName
            );
          }
        }),
        switchMap(([_, hasMultipleBranches]) =>
          hasMultipleBranches
            ? this.branchSelectionService.branchSelected
            : of({ selectedBranch: this.companyId })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ selectedBranch }) => {
        if (selectedBranch) {
          console.log('got here');
          this.loadData(selectedBranch).subscribe();
        } else if (!selectedBranch) {
          this.branchSelectionService.setSelectedBranch(this.companyId!);
          this.loadData(this.companyId).subscribe();
        }
      });
  }

  private subscribeToBranchSelectionChanges(): void {
    this.branchSelectionService.selectedBranch$
      .pipe(
        distinctUntilChanged(), // Only emit when the current value is different than the last
        takeUntil(this.destroy$)
      )
      .subscribe((selectedBranch) => {
        if (selectedBranch) {
          this.loadData(selectedBranch).subscribe();
        }
      });
  }

  private handleSingleBranchScenario(): void {
    this.canActivate.multipleBranches$
      .pipe(
        take(1),
        filter((hasMultipleBranches) => !hasMultipleBranches),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.companyId) {
          this.branchSelectionService.setSelectedBranch(this.companyId);
        }
      });
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      myControl: new UntypedFormControl('', [Validators.required]),
    });

    const selectedOption = this.selectedCompanyTS.getSelectedOption();
    if (selectedOption) {
      this.form.controls['myControl'].setValue(selectedOption);
      this.loadDataTechSupport();
    }

    this.filteredOptions = this.form.controls['myControl'].valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value.businessId)),
      switchMap((businessId) =>
        businessId ? this.loadCompanies(businessId) : of(this.options.slice())
      )
    );
  }

  /* istanbul ignore next */
  optionSelected(option: any) {
    this.selectedCompanyTS.setSelectedOption(option);
    this.loadDataTechSupport();
  }

  loadData(selectedBranch?: string | undefined | null) {
    this.loadingService.show();
    this.loadData$.next();
    this.loading = true;
    console.log('loadData called', selectedBranch);

    // Use forkJoin to wait for both API calls to complete
    return forkJoin([
      this.backend.getInventoryHistory(selectedBranch),
      this.backend.getRollup(selectedBranch),
    ]).pipe(
      takeUntil(this.loadData$),
      // takeUntil(1),
      tap(([results, rollup]) => {
        this.loadingService.hide();

        // Process the results of the first API call (getInventoryHistory)
        this.classifyResults(results);
        this.noTransferBox(results);

        // Process the results of the second API call (getRollup)
        this.classifyRollupResults(rollup);
      })
    );
  }
  /* istanbul ignore next */
  classifyRollupResults(results: LicensesHistory) {
    this.oneYearTotal = 0;
    this.fiveYearTotal = 0;

    results.rollup.forEach(
      (rollup: { duration: number; available: number }) => {
        if (rollup.duration === 1) {
          this.oneYearTotal += rollup.available;
        } else if (rollup.duration === 5) {
          this.fiveYearTotal += rollup.available;
        } else {
          // Do nothing
        }
      }
    );
  }

  /* istanbul ignore next */
  classifyResults(results: LicensesHistory) {
    this.oneYearCreditHistory = [];
    this.fiveYearCreditHistory = [];
    results.history.forEach((result) => {
      if (result.duration === 1) {
        this.oneYearCreditHistory.push(result);
      } else if (result.duration === 5) {
        this.fiveYearCreditHistory.push(result);
      } else {
        // Do nothing
      }
    });
  }

  /* istanbul ignore next */
  loadDataTechSupport() {
    this.loadingService.show();

    let userCompanySelection = {
      accountId: this.form.controls['myControl'].value.accountId,
      accountNumber: this.form.controls['myControl'].value.accountNumber,
      businessId: this.form.controls['myControl'].value.businessId,
    };

    const supportRollup$ = this.backend.getSupportRollup(userCompanySelection);
    const supportInventoryHistory$ =
      this.backend.getSupportInventoryHistory(userCompanySelection);

    forkJoin([supportRollup$, supportInventoryHistory$])
      .pipe(delay(500))
      .subscribe(
        ([rollupResults, inventoryHistoryResults]: [
          LicensesHistory,
          LicensesHistory
        ]) => {
          this.classifyRollupResultsTechSupport(rollupResults);
          this.classifyResultsTechSupport(inventoryHistoryResults);
          this.noTransferBox(inventoryHistoryResults);
        }
      );
    this.loadingService.hide();
  }

  /* istanbul ignore next */
  classifyRollupResultsTechSupport(results: LicensesHistory) {
    this.loadingService.show();
    this.oneYearCreditHistory = [];
    this.fiveYearCreditHistory = [];
    this.oneYearTotal = 0;
    this.fiveYearTotal = 0;
    results.rollup.forEach((rollup) => {
      if (rollup.duration === 1) {
        this.oneYearTotal += rollup.available;
      } else if (rollup.duration === 5) {
        this.fiveYearTotal += rollup.available;
      }
    });
    this.loadingService.hide();
  }
  /* istanbul ignore next */
  classifyResultsTechSupport(results: LicensesHistory) {
    this.loadingService.show();
    this.oneYearCreditHistory = [];
    this.fiveYearCreditHistory = [];

    results.history.forEach((result) => {
      if (result.duration === 1) {
        this.oneYearCreditHistory.push(result);
      } else if (result.duration === 5) {
        this.fiveYearCreditHistory.push(result);
      }
    });
    this.loadingService.hide();
  }

  /* istanbul ignore next */
  noTransferBox(results: LicensesHistory) {
    this.fiveYearRecentTransactions = [];
    this.oneYearRecentTransactions = [];
    results.history.forEach((result) => {
      if (result.transactions.length && result.duration === 5) {
        this.fiveYearRecentTransactions.push(...result.transactions);
      } else if (result.transactions.length && result.duration === 1) {
        this.oneYearRecentTransactions.push(...result.transactions);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCompanies(value: string): Observable<CompanySearch[]> {
    if (!value) {
      return of([]); // Don't call the API if the input is empty
    }
    this.loadingService.show();
    return this.backend.searchAccounts(value).pipe(
      tap((results: CompanySearch[]) => {
        this.options = results;
        this.accountsService.accounts.next(results);
        this.noResultsFound = results.length === 0;
        this.form.controls['myControl'].setErrors(
          this.noResultsFound ? { noResults: true } : null
        );
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of([]); // Return an empty observable in case of an error
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /* istanbul ignore next */
  displayName(subject: any): string {
    if (!subject) {
      return '';
    }
    if (subject.businessId) {
      return (
        subject.businessId +
        ' (Account #: ' +
        (subject.accountNumber ? subject.accountNumber : 'N/A') +
        ')' +
        '\n' +
        ' ' +
        subject.addressCity +
        ', ' +
        subject.addressState
      );
    } else if (subject.businessId) {
      return subject.businessId;
    } else {
      return '';
    }
  }
  /* istanbul ignore next */
  showSection(section: string) {
    console.log('showSection called', section);
    this.currentSection = section;

    if (section === 'inventory') {
      // Fetch permissions and decide which data loading function to call
      this.auth
        .getPermissions()
        .pipe(take(1))
        .subscribe((permissions) => {
          if (
            permissions.includes('Internal Admin') ||
            permissions.includes('Engage Support')
          ) {
            this.loadDataTechSupport();
          } else {
            const selectedBranch =
              this.branchSelectionService.getSelectedBranch();
            if (selectedBranch) {
              console.log('Loading data for selected branch:', selectedBranch);
              this.loadData(selectedBranch).subscribe({
                next: () => {
                  console.log(
                    'Data loaded successfully for inventory section.'
                  );
                },
                error: (error) => {
                  console.error(
                    'Error loading data for inventory section:',
                    error
                  );
                },
              });
            } else {
              console.warn('No branch selected. Cannot load inventory data.');
            }
          }
        });
    }
  }
}
